import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PageSkeleton } from '@/components/ui/PageSkeleton';
import { logger } from '@/utils/productionLogger';

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * Composant RequireAuth - Protection de route pour les pages nécessitant une authentification
 * 
 * Ce composant protège les routes qui nécessitent que l'utilisateur soit connecté.
 * Si l'utilisateur n'est pas authentifié, il est redirigé vers la page de connexion
 * avec un message et la possibilité de revenir après connexion.
 * 
 * Utilisation :
 * ```tsx
 * <Route
 *   path="/dashboard"
 *   element={
 *     <RequireAuth>
 *       <Dashboard />
 *     </RequireAuth>
 *   }
 * />
 * ```
 * 
 * @param children - Composant enfant à protéger
 */
export const RequireAuth = ({ children }: RequireAuthProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading } = useAuth();

  useEffect(() => {
    // Attendre que l'authentification soit chargée
    if (loading) return;

    // Si pas d'utilisateur connecté, rediriger vers la page de connexion
    if (!user) {
      logger.warn('RequireAuth: User not authenticated, redirecting to auth', {
        attemptedPath: location.pathname,
        hasUser: !!user,
        hasProfile: !!profile
      });
      
      navigate('/auth', {
        state: {
          from: location.pathname,
          message: 'Vous devez être connecté pour accéder à cette page'
        },
        replace: true
      });
      return;
    }
  }, [user, profile, loading, navigate, location]);

  // Afficher un skeleton pendant la vérification de l'authentification
  if (loading) {
    return <PageSkeleton />;
  }

  // Si pas d'utilisateur, ne rien afficher (redirection en cours)
  if (!user) {
    return null;
  }

  // Utilisateur authentifié avec profile valide, afficher le contenu avec animation fade-in
  return (
    <div className="animate-in fade-in duration-300 w-full h-full">
      {children}
    </div>
  );
};
